import React, { useState, useEffect } from 'react';
import { Link, NavLink, Navigate, Routes, Route, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Orders from './Orders';
import OrderDetails from './OrderDetails';
import Addresses from './Addresses';
import Profile from './Profile';
import ChangePassword from './ChangePassword';
import Reviews from './Reviews';
import Wishlist from './Wishlist';
import { resetCart } from '../../feature/cartSlice';
import { resetWishlist } from '../../feature/wishlist';
const BASE_API = (process.env.REACT_APP_BASE_API || '').replace(/\/+$/, '');

// last logged-in user saved by Login / Signup, so the name shows instantly before the API answers
const savedUser = () => {
    try {
        return JSON.parse(localStorage.getItem('user')) || { name: '', email: '' };
    } catch {
        return { name: '', email: '' };
    }
};

// true when the logged-in user is an admin (checks the common field names)
const isAdminUser = (u) =>
    String(u?.role ?? u?.user_role ?? u?.type ?? '').toLowerCase() === 'admin' ||
    u?.isAdmin === true ||
    u?.is_admin === true ||
    u?.is_admin === 1;

const Icon = ({ children }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);

const menu = [
    { label: 'My Orders', to: '/myaccount/orders', icon: <Icon><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M4 9h16" /></Icon> },
    { label: 'Addresses', to: '/myaccount/addresses', icon: <Icon><path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></Icon> },
    { label: 'Wishlist', to: '/myaccount/wishlist', icon: <Icon><path d="M12 20s-8-5-8-11a4.5 4.5 0 018-2.8A4.5 4.5 0 0120 9c0 6-8 11-8 11z" /></Icon> },
    { label: 'Profile', to: '/myaccount/profile', icon: <Icon><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></Icon> },
    { label: 'Change Password', to: '/myaccount/password', icon: <Icon><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></Icon> },
    { label: 'Reviews', to: '/myaccount/reviews', icon: <Icon><path d="M12 3l2.7 5.5 6 .9-4.4 4.2 1 6-5.3-2.8-5.3 2.8 1-6L3.3 9.4l6-.9z" /></Icon> },
];

// shown only to admins
const adminItem = {
    label: 'Dashboard',
    to: '/admin',
    icon: (
        <Icon>
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
        </Icon>
    ),
};

const AccountLayout = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [search, setSearch] = useState('');
    const [user, setUser] = useState(savedUser);

    // login is checked by <Protectroute> in AppRoutes, so the old TODO check is not needed here

    // load the real logged-in user from the API (GET /users/me)
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        const controller = new AbortController();

        const loadUser = async () => {
            try {
                const res = await fetch(`${BASE_API}/users/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (res.status === 401) {
                    // token expired or invalid
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    dispatch(resetCart());
                    navigate('/login', { replace: true });
                    return;
                }
                if (!res.ok) return;
                const data = await res.json();
                setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            } catch (err) {
                /* keep showing the saved user if the request fails */
            }
        };

        loadUser();
        return () => controller.abort();
    }, [dispatch, navigate]);

    // Profile page saved new details -> refresh the name / email in the sidebar
    useEffect(() => {
        const refresh = () => setUser(savedUser());
        window.addEventListener('user-updated', refresh);
        return () => window.removeEventListener('user-updated', refresh);
    }, []);

    const initials =
        (user.name || '').split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || 'U';

    // Dashboard link is added at the top of the menu for admins only
    const menuItems = isAdminUser(user) ? [adminItem, ...menu] : menu;

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        dispatch(resetCart());
        dispatch(resetWishlist());
        navigate('/login');
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (search.trim()) navigate(`/search?q=${encodeURIComponent(search.trim())}`);
    };

    return (
        <div className="min-h-screen bg-[#f3f4ef] text-gray-800 pt-16">
            {/* <header className="flex items-center gap-4 border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
                <Link to="/" className="flex shrink-0 items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2f6b1f] text-sm font-bold text-white">J</span>
                    <span className="text-lg font-semibold">Jeetayu</span>
                </Link>
                <form onSubmit={handleSearch} className="mx-auto hidden w-full max-w-xl sm:block">
                    <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 focus-within:border-[#2f6b1f]">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search"
                            placeholder="Search for Ayurveda, wellness, clothing & more..."
                            className="w-full bg-transparent text-xs outline-none placeholder-gray-400"
                        />
                    </div>
                </form>
                <div className="ml-auto flex items-center gap-4 text-gray-700 sm:ml-0">
                    <Link to="/account/profile" aria-label="Account"><Icon><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></Icon></Link>
                    <Link to="/cart" aria-label="Cart"><Icon><path d="M3 4h2l2.5 11h10L20 7H6" /><circle cx="9" cy="19" r="1.5" /><circle cx="17" cy="19" r="1.5" /></Icon></Link>
                </div>
            </header> */}

            <div className="mx-auto flex max-w-6xl flex-col md:flex-row">
                <aside className="border-b border-gray-200 bg-white/60 md:min-h-[calc(100vh-57px)] md:w-64 md:shrink-0 md:border-b-0 md:border-r">
                    <div className="flex items-center gap-3 px-5 py-5">
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold">{initials}</span>
                        <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">{user.name}</p>
                            <p className="truncate text-xs text-gray-500">{user.email}</p>
                        </div>
                    </div>
                    <nav className="flex gap-1 overflow-x-auto px-3 pb-3 md:flex-col md:overflow-visible" aria-label="Account">
                        {menuItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) =>
                                    `flex shrink-0 items-center gap-3 rounded-md border-l-[3px] px-3 py-2.5 text-sm transition ${
                                        isActive
                                            ? 'border-[#2f6b1f] bg-[#e9ede4] font-medium text-gray-900'
                                            : 'border-transparent text-gray-600 hover:bg-gray-100'
                                    }`
                                }
                            >
                                {item.icon}
                                {item.label}
                            </NavLink>
                        ))}
                        <button onClick={handleLogout} className="flex shrink-0 items-center gap-3 rounded-md border-l-[3px] border-transparent px-3 py-2.5 text-left text-sm text-gray-600 hover:bg-gray-100">
                            <Icon><path d="M10 4H5v16h5" /><path d="M14 8l4 4-4 4" /><path d="M18 12H9" /></Icon>
                            Logout
                        </button>
                    </nav>
                </aside>

                {/* The page for the current URL renders here */}
                <main className="flex-1 px-4 py-6 sm:px-8">
                    <Routes>
                        <Route index element={<Navigate to="/myaccount/orders" replace />} />
                        <Route path="orders" element={<Orders />} />
                        <Route path="orders/:id" element={<OrderDetails />} />
                        <Route path="addresses" element={<Addresses />} />
                        <Route path="wishlist" element={<Wishlist />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="password" element={<ChangePassword />} />
                        <Route path="reviews" element={<Reviews />} />
                    </Routes>
                </main>
            </div>
        </div>
    );
};

export default AccountLayout;
